import Long from 'long';
import { fetchFileBuffer } from '../../fetcher';
import { SingletonLoader } from './SingletonLoader';
import unifiedWorkerPool from '../../utils/workers/UnifiedWorkerPool';
import workerResponseQueue from './WorkerResponseQueue';
import { stats } from '../../potreeState';
import * as potreeState from '../../potreeState';
import { PPCLoaderDebugWidget } from '../../utils/PPCLoaderDebugWidget';

// Track profiling if widget is loaded
function trackWorkerStart() {
  if (typeof window.PPCLoaderDebugWidget !== 'undefined') {
    PPCLoaderDebugWidget.trackWorkerStart();
  }
}

function trackWorkerEnd() {
  if (typeof window.PPCLoaderDebugWidget !== 'undefined') {
    PPCLoaderDebugWidget.trackWorkerEnd();
  }
}

function trackWorkerCallback() {
  if (typeof window.PPCLoaderDebugWidget !== 'undefined') {
    PPCLoaderDebugWidget.trackWorkerCallback();
  }
}

export enum EnumDataFileType {
  'XYZ' = 'XYZ',
  'RGB' = 'RGB',
  'INTENSITY' = 'INTENSITY',
  'CLASSIFICATION' = 'CLASSIFICATION',
  'DIFFERENCE' = 'DIFFERENCE',
  'SEGMENT' = 'SEGMENT',
  'SOURCE' = 'SOURCE',
}
export enum EnumHierarchyFileType {
  'CHILD_INFO' = 'CHILD_INFO',
  'TILE_3D_OFFSET' = 'TILE_3D_OFFSET',
}

export type EnumFileType = EnumDataFileType | EnumHierarchyFileType;

export enum EnumFileExtension {
  'PVCH' = 'pvch',
  'PTIL' = 'ptil',
  'PXYZ' = 'pxyz',
  'PRGB' = 'prgb',
  'PINT' = 'pint',
  'PCLA' = 'pcla',
  'PDIF' = 'pdif',
  'PSEG' = 'pseg',
  'PSID' = 'psid',
}

const EXTENSIONS_BY_FILE_TYPE = {
  [EnumHierarchyFileType.CHILD_INFO]: EnumFileExtension.PVCH,
  [EnumHierarchyFileType.TILE_3D_OFFSET]: EnumFileExtension.PTIL,
  [EnumDataFileType.XYZ]: EnumFileExtension.PXYZ,
  [EnumDataFileType.RGB]: EnumFileExtension.PRGB,
  [EnumDataFileType.INTENSITY]: EnumFileExtension.PINT,
  [EnumDataFileType.CLASSIFICATION]: EnumFileExtension.PCLA,
  [EnumDataFileType.DIFFERENCE]: EnumFileExtension.PDIF,
  [EnumDataFileType.SEGMENT]: EnumFileExtension.PSEG,
  [EnumDataFileType.SOURCE]: EnumFileExtension.PSID,
};

const getURL = (args: { folder: string; level: number; fileIdentifier: Long; extension: EnumFileExtension }) => {
  const { folder, level, extension, fileIdentifier } = args;
  const levelStr = ('0' + level).slice(-2);
  // The SAS is requested and cached per project; the blobs live in the project's storage container.
  const SASToken = localStorage.getItem('SASToken_' + potreeState.projectId);
  return `${potreeState.storageContainer}/${folder}/${levelStr}/${fileIdentifier}.${extension}?${SASToken}`;
};

export interface IPPCFileArgs {
  folder: string;
  level: number;
  fileIdentifier: Long;
  fileType: EnumFileType;
}

export class PPCFile {
  folder: string;
  level: number;
  fileIdentifier: Long;
  fileType: EnumFileType;
  extension: EnumFileExtension | null;
  url: string;
  fileData?: number[][];
  filePromise: Promise<unknown> | undefined;
  private singletonLoaderDecompress: SingletonLoader = new SingletonLoader();
  private singletonLoaderFetchFile: SingletonLoader = new SingletonLoader();

  constructor(args: IPPCFileArgs) {
    const { folder, level, fileIdentifier, fileType } = args;
    this.fileType = fileType;
    this.folder = folder;
    this.level = level;
    this.fileIdentifier = fileIdentifier;
    this.extension = EXTENSIONS_BY_FILE_TYPE[this.fileType];
    this.url = getURL({ extension: this.extension, fileIdentifier, folder, level });
  }

  async load({ pointsAmount }: { pointsAmount: number }): Promise<number[]> {
    const fileBuffer = await this.singletonLoaderFetchFile.load(fetchFileBuffer, this.url, { cacheTime: 30000 });
    if (!fileBuffer) return [];
    const result = await this.singletonLoaderDecompress.load(this.loadFile.bind(this), { pointsAmount, fileBuffer });
    const loadResult = result as { data: number[] } | undefined;
    return loadResult?.data ?? [];
  }

  private async loadFile(args: { pointsAmount: number; fileBuffer: ArrayBuffer }) {
    const { pointsAmount, fileBuffer } = args;
    return await new Promise((resolve, reject) => {
      const buffer = fileBuffer;
      if (!buffer) {
        reject(new Error('No file buffer provided'));
        return;
      }

      const card32Array = new Uint32Array(buffer.slice(256, 320));
      const startPos = card32Array[2];
      const length = card32Array[3];
      const bufferSlice = buffer.slice(startPos, startPos + length + 8);
      const wwId = `${Math.random()}_${+new Date()}`;
      // Use the public copy directly to bypass Vite's bundling.
      // The worker uses importScripts which is incompatible with module workers.
      const workerPath = '/potree/wasm/PPCWorker.js';
      unifiedWorkerPool
        .getWorker(workerPath)
        .then((entry) => {
          trackWorkerStart();

          interface WorkerMessageData {
            list?: number[][];
            error?: string;
            logTime?: number;
          }

          entry.worker.onmessage = (e: MessageEvent<WorkerMessageData>) => {
            trackWorkerCallback();

            // Queue the response processing to smooth out load
            workerResponseQueue.enqueue(() => {
              if (!e.data) {
                console.error('Worker returned no data');
                unifiedWorkerPool.returnWorker(workerPath, entry);
                trackWorkerEnd();
                reject(new Error('Worker returned no data'));
                return;
              }
              if (e.data.error) {
                console.error('Worker error:', e.data.error);
                unifiedWorkerPool.returnWorker(workerPath, entry);
                trackWorkerEnd();
                reject(new Error(e.data.error));
                return;
              }
              resolve({ data: e.data.list });
              if (e.data.logTime) stats.decodings.push(e.data.logTime);
              unifiedWorkerPool.returnWorker(workerPath, entry);
              trackWorkerEnd();
            });
          };
          entry.worker.onerror = (e: ErrorEvent) => {
            console.error('Worker error loading file:', e);
            unifiedWorkerPool.discardWorker(entry);
            trackWorkerEnd();
            reject(new Error('Worker error: ' + (e.message || 'Unknown error')));
          };

          const message = {
            buffer: bufferSlice,
            fileExtension: this.extension,
            fileOffset: 0,
            numPoints: pointsAmount,
            dataLevel: this.level,
            useWasm: !window.location.href.includes('disableWasm=true'),
            id: wwId,
            fileUrl: `${this.folder}/${this.level}/${this.fileIdentifier.low}.${this.extension}`,
          };

          try {
            entry.worker.postMessage(message, [message.buffer]);
          } catch (ex) {
            console.error('Error posting message to worker:', ex);
            unifiedWorkerPool.returnWorker(workerPath, entry);
            trackWorkerEnd();
            reject(ex);
          }
        })
        .catch(reject);
    });
  }
}
